import { Connection } from '@app/services/export/ppt/classes/Connection';
import { ConnectionNavigation } from '@app/services/export/ppt/classes/ConnectionNavigation';
import { Map } from '@app/services/export/ppt/classes/Map';
import { INumberNavigationItem, NumberNavigation } from '@app/services/export/ppt/classes/NumberNavigation';
import { RoutePriceTable } from '@app/services/export/ppt/classes/RoutePriceTable';
import { SeasonCalendar } from '@app/services/export/ppt/classes/SeasonCalendar';
import PowerPointPresentation from '@app/services/export/ppt/PowerPointPresentation';
import AbstractOfferSlideProcessor, {
	IPptOfferSlideOptions
} from '@app/services/export/ppt/slides/AbstractOfferSlideProcessor';
import { richTextToPptxText } from '@app/services/export/ppt/utils/formattingUtils';
import { gridColWidth } from '@app/services/export/ppt/utils/gridUtils';
import { getChunkedListForAvailableSpace } from '@app/services/export/ppt/utils/listUtils';
import {
	colors,
	defaultViewPortPadding,
	elementSizes,
	presentationDimensions
} from '@app/services/export/ppt/variables/constants';
import { copy, copySmall, copySmallBold } from '@app/services/export/ppt/variables/typography';
import ComfortClass from '@app/types/base/ComfortClass';
import IConnection from '@app/types/data/base/IConnection';
import IConnectionNavigationItem from '@app/types/data/base/IConnectionNavigationItem';
import IRoutePricesSlideData from '@app/types/data/slides/IRoutePricesSlideData';
import PptxGenJS from 'pptxgenjs';

export default class PowerPointRoutePricesSlideProcessor extends AbstractOfferSlideProcessor<IRoutePricesSlideData> {
	private readonly paginationMargin = 0.2;

	protected masterName = this.slideData.id;

	public constructor(slideData: IRoutePricesSlideData, presentation: PowerPointPresentation) {
		const options: Partial<IPptOfferSlideOptions> = {
			imageWidth: defaultViewPortPadding.horizontal + gridColWidth(6),
			textY: 0.2
		};

		super(slideData, presentation, options);
	}

	public async processSlideData(): Promise<void> {
		if (this.slideData.image) {
			await this.adjustImageDimensions(this.slideData.image);
		}

		if (this.slideData.image) {
			this.presentation.defineSlideMaster({
				title: this.masterName,
				objects: Map.createMapMasterObjectsForImage(
					this.slideData.image,
					this.options.imageWidth,
					presentationDimensions.h
				)
			});
		}

		this.createOverviewSlide();
		this.createSubSlides();
	}

	private createOverviewSlide(): void {
		this.updateSlideData({ visibleSvgGroup: 'default' });

		this.getChunks().forEach((list, chunkIndex) => {
			const slideBaseNumber = this.presentation.getSlideNumberById(this.slideData.id);
			const { x, y, w, h } = this.getAvailableSpaceRectangle();
			const offerSlideBase: PptxGenJS.Slide = this.createOfferSlideBase({
				omitTopBar: true
			});

			const chunksBefore = this.getChunks().slice(0, chunkIndex);
			const sumOfItemsBefore = chunksBefore.reduce((sum, chunk) => sum + chunk.length, 0);

			const navigationStartOffset = slideBaseNumber + this.getChunks().length + sumOfItemsBefore;

			const connectionNavigation = new ConnectionNavigation(
				offerSlideBase,
				{ list, navigationStartOffset },
				{ x, y, w }
			);

			connectionNavigation.create();

			if (this.getChunks().length > 1) {
				const pagination = new NumberNavigation(
					offerSlideBase,
					{
						links: this.getPaginationLinks(),
						currentIndex: chunkIndex
					},
					{
						x,
						y: y + h - elementSizes.paginationHeight - this.paginationMargin,
						w
					}
				);

				pagination.create();
			}
		});
	}

	private createSubSlides(): void {
		const connectionTextY = 0.8;
		const connectionSubTextH = 0.5;
		const routePriceTableY = 1.9;

		this.updateSlideData({ copy: '', headline: '' });

		this.getChunks().forEach((connectionChunk, chunkIndex) => {
			connectionChunk.forEach((item) => {
				this.updateSlideData({
					visibleSvgGroup: item.connection.svgId,
					operatingTimes: item.connection.operatingTimes,
					priceValidity: this.slideData.priceValidity
				});

				const { x, w } = this.getAvailableSpaceRectangle();
				const offerSlideBase: PptxGenJS.Slide = this.createOfferSlideBase();

				const routePriceTable = new RoutePriceTable(
					offerSlideBase,
					{ connection: item.connection },
					{ x, y: routePriceTableY, w }
				);

				routePriceTable.create();

				if (item.connection.seasons && item.connection.seasons.length > 1) {
					const seasonCalendar = new SeasonCalendar(
						offerSlideBase,
						{
							seasons: item.connection.seasons
						},
						{ x, w }
					);

					seasonCalendar.create();
				}

				// back link
				const backLinkDiameter = 0.25;
				offerSlideBase.addText('✖', {
					...copySmallBold,
					lineSpacingMultiple: 1,
					shape: 'roundRect',
					rectRadius: backLinkDiameter / 2,
					x: presentationDimensions.w - backLinkDiameter - 0.1,
					y: elementSizes.metaHeaderHeight / 2 - backLinkDiameter / 2,
					w: backLinkDiameter,
					h: backLinkDiameter,
					color: colors.white,
					fill: { color: colors.blue },
					align: 'center',
					valign: 'middle',
					hyperlink: { slide: this.presentation.getSlideNumberById(this.slideData.id) + chunkIndex },
					underline: { color: colors.blue },
					margin: 0
				});

				// connection headline
				const connectionHeadline = new Connection(offerSlideBase, {
					connection: item.connection,
					variant: 'big',
					maxWidth: w
				});
				connectionHeadline.createAtCoordinates(x, connectionTextY);

				// subtext
				if (
					item &&
					item.connection.priceGroups &&
					item.connection.priceGroups[0] &&
					item.connection.priceGroups[0].comfortClass === ComfortClass.SecondClass
				) {
					offerSlideBase.addText(this.i18n.secondClass, {
						...copy,
						x,
						y: connectionTextY + connectionHeadline.getDimensions().h,
						w,
						h: connectionSubTextH,
						valign: 'top',
						align: 'left',
						color: colors.blue,
						margin: 0
					});
				}

				// price info
				const priceInfoHeight = 0.4;
				const priceInfoY = routePriceTable.getDimensionRect().bottom + 0.1;

				if (item && item.connection.priceGroups && item.connection.priceGroups[0]) {
					const text = item.connection.priceGroups[0].priceInfo;

					if (!text) return;

					offerSlideBase.addText(text, {
						...copy,
						x,
						y: priceInfoY,
						w,
						h: priceInfoHeight,
						valign: 'top',
						align: 'right',
						color: colors.blue,
						margin: 0
					});
				}

				const maintenanceInformation = this.mapMaintenanceTimes(item.connection.operatingTimes);

				// maintenance information
				if (maintenanceInformation) {
					const maintenanceInformationHeight = 0.65;

					offerSlideBase.addText(richTextToPptxText(maintenanceInformation), {
						...copySmall,
						x,
						y: priceInfoY + priceInfoHeight,
						w,
						h: maintenanceInformationHeight,
						valign: 'top',
						align: 'left',
						color: colors.blue,
						margin: 0
					});
				}
			});
		});
	}

	protected getChunks(): IConnectionNavigationItem[][] {
		return getChunkedListForAvailableSpace(
			<IConnectionNavigationItem[]>this.getNavigationLinks(),
			this.getAvailableSpaceRectangle().h,
			{
				rowHeight: ConnectionNavigation.itemHeight + ConnectionNavigation.itemGap
			}
		);
	}

	private getPaginationLinks(): INumberNavigationItem[] {
		const links: INumberNavigationItem[] = [];

		this.getChunks().forEach((connectionChunk, chunkIndex) => {
			const slideNumberLink = this.presentation.getSlideNumberById(this.slideData.id) + chunkIndex;

			links.push({ numberLabel: chunkIndex + 1, slideNumberLink });
		});

		return links;
	}

	/**
	 * ConnectionNavigation bases on IConnectionNavigationItem[] because of OfferC.
	 */
	private getNavigationLinks(): IConnectionNavigationItem[] {
		return this.slideData.list.map(
			(connection: IConnection): IConnectionNavigationItem => ({
				connection,
				description: connection.connectingTicketInfo
			})
		);
	}

	protected getSlideCount(): number {
		return this.getChunks().length + this.slideData.list.length;
	}
}
